import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useDropzone } from 'react-dropzone';
import {
	Box,
	Button,
	Image,
	Input,
	Text,
	Heading,
	useToast,
} from '@chakra-ui/react';
import uploadIcon from '../../../assets/upload_icon.png';
import AutoTypeText from './autotype';
import { useSelector } from 'react-redux';
import { saveAs } from 'file-saver';
import excelFile from '../../../assets/master/assets.csv';
import AssetsPaginatedData from './assetsPaginatedData';

const Assets = () => {
	const toast = useToast();
	const [data, setData] = useState([]);
	const [storage, setStorage] = useState([]);
	const [editing, setEditing] = useState(false);
	const [errorValue, setErrorValue] = useState();
	const locationCode = useSelector((state) => state.locationCode);
	const companyId = useSelector((state) => state.companyId);
	const [uploadMessageExcel, setUploadMessageExcel] = useState(undefined);
	const [hasValidationErrors, setHasValidationErrors] = useState(false);
	const [errors, setErrors] = useState([]);
	const [pagesWithErrors, setPagesWithErrors] = useState(new Set());
	const [currentPage, setCurrentPage] = useState(1); // Current page
	const itemsPerPage = 50; // Number of items to display per page
	const [storageDep, setStorageDep] = useState([]);

	function toastCallApiError() {
		return toast({
			title: 'Failed to load',
			status: 'error',
			duration: 3000,
			isClosable: true,
		});
	}
	function toastCallRequestError() {
		return toast({
			title: 'Request Failed',
			status: 'error',
			duration: 3000,
			isClosable: true,
		});
	}

	function formatDateToDdMmYy(date) {
		const day = String(date.getDate()).padStart(2, '0'); // Get day and pad with leading zero if needed
		const month = String(date.getMonth() + 1).padStart(2, '0'); // Get month (add 1 as months are zero-indexed) and pad with leading zero if needed
		const year = String(date.getFullYear()).slice(-2); // Get the last 2 digits of the year

		return `${day}-${month}-${year}`;
	}
	const currentDate = new Date();
	const formattedDate = formatDateToDdMmYy(currentDate);

	useEffect(() => {
		const rawMaterial = async () => {
			try {
				const response1 = await fetch(
					`${process.env.REACT_APP_API_URL}api-uom.php?company_id=${companyId}`,
					{
						method: 'GET',
					}
				);
				if (response1.ok) {
					const data1 = await response1.json();
					setStorage(data1.data);
				}
			} catch {
				toastCallApiError();
			}
		};
		rawMaterial();
	}, []);

	useEffect(() => {
		const depreciationkey = async () => {
			try {
				const response1 = await fetch(
					`${process.env.REACT_APP_API_URL}api-depreciation-list.php?company_id=${companyId}`,
					{
						method: 'GET',
					}
				);
				if (response1.ok) {
					const data1 = await response1.json();
					setStorageDep(data1.data);
				}
			} catch {
				toastCallApiError();
			}
		};
		depreciationkey();
	}, []);

	const handleFileDrop = useCallback((acceptedFiles) => {
		const file = acceptedFiles[0];
		const reader = new FileReader();

		reader.onload = (event) => {
			const data = new Uint8Array(event.target.result);
			const workbook = XLSX.read(data, { type: 'array' });

			const sheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[sheetName];

			const excelData = XLSX.utils.sheet_to_json(worksheet, {
				header: 1,
			});

			const headers = excelData[0].map((header) => header.trim());
			const formattedData = excelData.slice(1).map((row) => {
				const rowData = {};
				row.forEach((cell, columnIndex) => {
					const header = headers[columnIndex];
					rowData[header] = cell;
				});
				return rowData;
			});

			// Fill missing keys with empty strings
			const emptyRow = Object.fromEntries(
				headers.map((header) => [header, ''])
			);
			const dataWithEmptyKeys = formattedData.map((row) => ({
				...emptyRow,
				...row,
			}));

			// Filter out blank rows
			const nonBlankRows = dataWithEmptyKeys.filter((row) => {
				return Object.values(row).some(
					(cellValue) => cellValue !== null && cellValue !== ''
				);
			});
			setData(nonBlankRows);
			setUploadMessageExcel(undefined);
		};

		reader.readAsArrayBuffer(file);
	});

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop: handleFileDrop,
		accept: '.xlsx',
	});

	const handleDownload = () => {
		const link = document.createElement('a');
		link.href = excelFile;
		link.setAttribute(
			'download',
			`Assets-${locationCode}-${formattedDate}.csv`
		);
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	const storageXlSXDep_Key = storageDep;
	function generateCSVData(data) {
		try {
			if (!data || data.length === 0) return '';

			// Get headers from the keys of the first object
			const headers = Object.keys(data[0]);
			const headerRow = headers.join(',') + '\n';

			const rows = data
				.map((item) =>
					headers
						.map((header) => {
							let value = item[header] ?? ''; // handle null/undefined
							value = value.toString().replace(/"/g, '""'); // escape quotes
							// Wrap in quotes if contains comma, newline, or quote
							if (/[",\n]/.test(value)) {
								value = `"${value}"`;
							}
							return value;
						})
						.join(',')
				)
				.join('\n');

			return headerRow + rows;
		} catch (err) {
			console.error('CSV generation failed:', err);
			toastCallRequestError();
			return '';
		}
	}

	const handleDownloadDep = () => {
		if (storageXlSXDep_Key && storageXlSXDep_Key.length > 0) {
			const updatedData = storageXlSXDep_Key.map((asset) => ({
				...asset,
				// Example: Add initial open balance if needed
				// open_balance: asset.open_balance ?? 0,
			}));

			const csvData = generateCSVData(updatedData);
			const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8' });
			saveAs(blob, `DepreciationKey-${locationCode}-${formattedDate}.csv`);
		} else {
			toastCallRequestError();
		}
	};


	// storage location
	const storageXlSX = storage;
	function generateCSVData(data) {
	try {
		if (!data || data.length === 0) return '';

		// Always fix column order explicitly
		const headers = ['desp_key', 'asset_class', 'asset_life', 'wdv', 'slm'];

		// Build header row
		const headerRow = headers.join(',') + '\n';

		// Build rows
		const rows = data
			.map((item) =>
				headers
					.map((header) => {
						let value = item[header] ?? ''; // empty if null/undefined
						value = value.toString().replace(/"/g, '""'); // escape quotes
						if (/[",\n]/.test(value)) {
							value = `"${value}"`; // wrap if comma/newline/quote
						}
						return value;
					})
					.join(',')
			)
			.join('\n');

		return headerRow + rows;
	} catch (err) {
		console.error('CSV generation failed:', err);
		toastCallRequestError();
		return '';
	}
}

	const storageHandleDownload = () => {
		if (storageXlSX !== undefined) {
			const updatedData = storageXlSX?.map((asset) => ({
				...asset,
				// You can set the initial open balance value here
			}));
			const csvData = generateCSVData(updatedData);
			const blob = new Blob([csvData], {
				type: 'text/csv;charset=utf-8',
			});
			saveAs(
				blob,
				`unit-of-measurement-${locationCode}-${formattedDate}.csv`
			);
		} else {
			toastCallRequestError();
		}
	};

	const handlePageChange = (newPage) => {
		setCurrentPage(newPage);
	};

	useEffect(() => {
		// Calculate the errorValue based on the errors state
		const newErrorValue = errors.reduce((acc, rowErrors) => {
			return acc + Object.values(rowErrors).filter((error) => error !== null).length
		}, 0);

		setErrorValue(newErrorValue);
	}, [errors]);


	return (
		<Box
			width='100%'
			height='100%'
			display='flex'
			justifyContent='center'
			alignItems='center'
			flexDirection='column'>
			<Box w='100%' display='flex' justifyContent='space-around'>
				<Box
					width='38%'
					display='flex'
					justifyContent='space-around'
					alignItems='center'>
					<Box width='400px'>
						<Box
							border='2px dashed var(--chakra-colors-bgGraycolor)'
							p='15px'
							borderRadius='20px'
							opacity={editing ? '0.5' : '1'}
							pointerEvents={editing ? 'none' : 'painted'}>
							<Box
								{...getRootProps()}
								bg='var(--chakra-colors-mainBlueColor)'
								width='100%'
								height='100%'
								borderRadius='20px'
								display='flex'
								justifyContent='center'
								alignItems='center'
								color='white'
								padding='50px 0px'
								cursor='grab'>
								<Input {...getInputProps()} />
								{isDragActive ? (
									<Box
										display='flex'
										flexDirection='column'
										justifyContent='center'
										alignItems='center'>
										<Image
											src={uploadIcon}
											alt='upload icon'
											height='100px'
											width='100px'
										/>
										<Text>Drop the Excel file here...</Text>
									</Box>
								) : (
									<Box
										display='flex'
										flexDirection='column'
										justifyContent='center'
										alignItems='center'>
										<Image
											src={uploadIcon}
											alt='upload icon'
											height='100px'
											width='100px'
										/>
										<Text>Drag & Drop your files</Text>
									</Box>
								)}
							</Box>
						</Box>
						<Box
							display="flex"
							width="100%"
							justifyContent="center"
							gap="15px"
							paddingTop="45px"
							flexWrap="wrap"
						>
							<Button
								borderRadius="10px"
								border="1px solid #003060"
								p="20px 30px"
								width="45%"
								fontSize="15px"
								background="none"
								color="mainBlueColor"
								onClick={handleDownload}
								cursor="pointer"
							>
								Download Sample
							</Button>

							<Button
								borderRadius="10px"
								border="1px solid #003060"
								p="20px 0px"
								width="45%"
								fontSize="15px"
								background="none"
								color="mainBlueColor"
								onClick={storageHandleDownload}
								cursor="pointer"
							>
								UOM Sample
							</Button>
							<Button
								borderRadius="10px"
								border="1px solid #003060"
								p="20px 0px"
								width="55%"
								fontSize="15px"
								background="none"
								color="mainBlueColor"
								onClick={handleDownloadDep}
								cursor="pointer"
							>
								Download Depreciationkey
							</Button>
						</Box>
					</Box>
				</Box>

				<Box
					width='58%'
					boxShadow='rgba(0, 0, 0, 0.24) 0px 3px 8px'
					borderRadius='15px'
					padding='15px'>
					<Heading
						mb='15px'
						color='var(--chakra-colors-mainBlueColor)'
						fontSize='28px'>
						Data Summary :{' '}
					</Heading>
					<Text
						fontSize='1.7rem'
						fontWeight='600'
						marginBottom='10px'
						color='mainBlueColor'>
						Total lines - {data?.length}
					</Text>
					<Text
						fontSize='1.7rem'
						fontWeight='600'
						marginBottom='10px'
						color='red'>
						Total Error : {errorValue}
					</Text>
					<Box mt='10px' mb='10px'>
						<Box
							fontSize='1.7rem'
							fontWeight='600'
							color='mainBlueColor'>
							Pages with errors:
						</Box>

						{Array.from(pagesWithErrors).join(', ')}
					</Box>
					<Box>
						<Text
							fontSize='1.7rem'
							fontWeight='600'
							color='mainBlueColor'
							marginBottom='5px'>
							Data Review Report -
						</Text>
						<AutoTypeText />
					</Box>
				</Box>
			</Box>
			<AssetsPaginatedData
				data={data}
				setData={setData}
				currentPage={currentPage}
				itemsPerPage={itemsPerPage}
				editing={editing}
				setEditing={setEditing}
				onPageChange={handlePageChange}
				setErrorValue={setErrorValue}
				errorValue={errorValue}
				errors={errors}
				setErrors={setErrors}
				hasValidationErrors={hasValidationErrors}
				setHasValidationErrors={setHasValidationErrors}
				setPagesWithErrors={setPagesWithErrors}
				uploadMessageExcel={uploadMessageExcel}
				setUploadMessageExcel={setUploadMessageExcel}
				storageXlSXDep_Key={storageDep}
			/>
		</Box>
	);
};

export default Assets;
